/**
 * Per-tile mist weights: 1 on a hidden tile, 0 on a revealed or settled one, easing between the
 * two for the fade after a reveal, once a banner settles, or, after death, on every tile within
 * reach of the revealed ground, so the player sees the ground there was and the chasms in it;
 * and back to 1 where the mist closes again for the journey.
 * The weights go into a small texture, one texel per tile, that the shader samples nearest (does
 * mist cover this pixel) and that the scene builder reads per tile (how far the column has risen).
 */
import type { WorldSource } from "./world-source.ts";

/** Time for a revealed tile's mist to clear, in milliseconds. From the prototype. */
export const FADE_MS = 450;

/**
 * Nearness to revealed ground per tile, from the weights of a `w` by `h` window: the plateau's
 * shadow on the mist floor around it, on every side, since the 3D world needs no trick to read
 * as raised ground (decision 0009). 1 for the ring of tiles right next to revealed ground, one
 * half for the ring two tiles out, 0 elsewhere. The shader interpolates this between tile
 * centres.
 */
export function nearness(weights: Uint8Array, w: number, h: number, out: Uint8Array): void {
  for (let j = 0; j < h; j += 1) {
    for (let i = 0; i < w; i += 1) {
      let best = 0;
      for (let dy = -2; dy <= 2; dy += 1) {
        const y = j + dy;
        if (y < 0 || y >= h) continue;
        for (let dx = -2; dx <= 2; dx += 1) {
          const x = i + dx;
          if (x < 0 || x >= w || (dx === 0 && dy === 0)) continue;
          const d = Math.max(Math.abs(dx), Math.abs(dy));
          const n = (255 - (weights[y * w + x] as number)) * (d === 1 ? 1 : 0.5);
          if (n > best) best = n;
        }
      }
      out[j * w + i] = Math.round(best);
    }
  }
}

function key(x: number, y: number): number {
  return (x + 1048576) * 2097152 + (y + 1048576);
}

function touchesRevealed(world: WorldSource, x: number, y: number): boolean {
  for (let dy = -1; dy <= 1; dy += 1) {
    for (let dx = -1; dx <= 1; dx += 1) {
      if ((dx || dy) && world.isRevealed(x + dx, y + dy)) return true;
    }
  }
  return false;
}

export class FogState {
  private readonly revealedAt = new Map<number, number>();
  /** When each settled banner, or tile lifted after death, was first seen so; stamped lazily by `weight`. */
  private readonly settledAt = new Map<number, number>();
  private settling = false;
  /** Tiles the mist closes over again, and when it began to: the journey, a trial of apps/web. */
  private closing: { readonly tiles: ReadonlySet<number>; readonly at: number } | null = null;
  private closingNow = false;
  /** A trial: gaps where a creature rises keep their mist after death. */
  keepMist: ((world: WorldSource, x: number, y: number) => boolean) | null = null;

  /** Starts the clearing animation for tiles just revealed. */
  markRevealed(tiles: readonly { x: number; y: number }[], now: number): void {
    for (const t of tiles) this.revealedAt.set(key(t.x, t.y), now);
  }

  /**
   * The mist closes over these tiles again from `now`, as it lifted off them, whatever the world
   * says of them, until `open`.
   */
  close(tiles: readonly { x: number; y: number }[], now: number): void {
    this.closing = { tiles: new Set(tiles.map((t) => key(t.x, t.y))), at: now };
  }

  /** The tiles closed are the world's again. */
  open(): void {
    this.closing = null;
    this.closingNow = false;
  }

  /** Forgets finished animations. Call once per frame. */
  prune(now: number): void {
    for (const [k, t] of this.revealedAt) if (now - t >= FADE_MS) this.revealedAt.delete(k);
    this.closingNow = this.closing !== null && now - this.closing.at < FADE_MS;
  }

  /** True while any tile is still clearing, so the app keeps rendering frames. */
  get animating(): boolean {
    return this.revealedAt.size > 0 || this.settling || this.closingNow;
  }

  /**
   * The mist weight of one tile at `now`. Hidden tiles weigh 1. A settled banner clears like a
   * revealed tile, from the frame that first sees it settled, and so does, after death, every
   * tile without a banner that touches revealed ground. A tile closed eases back to 1.
   */
  weight(world: WorldSource, x: number, y: number, now: number, reducedMotion: boolean): number {
    if (this.closing?.tiles.has(key(x, y))) {
      if (reducedMotion) return 1;
      const p = Math.min(1, Math.max(0, (now - this.closing.at) / FADE_MS));
      return p * p * (3 - 2 * p);
    }
    if (world.isRevealed(x, y)) {
      if (reducedMotion) return 0;
      const t = this.revealedAt.get(key(x, y));
      if (t === undefined) return 0;
      const p = Math.min(1, Math.max(0, (now - t) / FADE_MS));
      return 1 - p * p * (3 - 2 * p);
    }
    const k = key(x, y);
    if (this.keepMist?.(world, x, y)) return 1;
    if (
      world.isSettled(x, y) ||
      world.isFallen?.(x, y) ||
      (world.status === "dead" && !world.hasBanner(x, y) && touchesRevealed(world, x, y))
    ) {
      if (reducedMotion) return 0;
      let t = this.settledAt.get(k);
      if (t === undefined) {
        t = now;
        this.settledAt.set(k, t);
      }
      const p = Math.min(1, Math.max(0, (now - t) / FADE_MS));
      if (p < 1) this.settling = true;
      return 1 - p * p * (3 - 2 * p);
    }
    if (this.settledAt.size > 0) this.settledAt.delete(k);
    return 1;
  }

  /**
   * Fills `out` (w * h bytes, row-major from tile (x0, y0)) with weights scaled to 0..255.
   */
  fill(
    out: Uint8Array,
    world: WorldSource,
    x0: number,
    y0: number,
    w: number,
    h: number,
    now: number,
    reducedMotion: boolean,
  ): void {
    this.settling = false;
    for (let j = 0; j < h; j += 1) {
      for (let i = 0; i < w; i += 1) {
        out[j * w + i] = Math.round(this.weight(world, x0 + i, y0 + j, now, reducedMotion) * 255);
      }
    }
  }
}
